import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AxisBottom, AxisRight } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { AnimatePresence, motion } from 'framer-motion';
import { pointOnPathAtX } from '@/lib/svg-path';
import { useTheme } from '@/store/themeStore';
import type { Theme } from '@/lib/theme/applyTheme';
import type { PriceHistoryPointDto } from '@/lib/models';
import { buildSeriesPoints, type SeriesPoint } from './market-chart-series';

/** A single Yes line on the market chart (one per market). */
export interface MarketChartLine {
  /** Stable key (market / outcome id). */
  id: string;
  /** Series display name (candidate / team / "Yes"). */
  label: string;
  /** Line color — cycles through a palette for multi-market. */
  color: string;
  /** Price-history points for this line's primary outcome. */
  history: PriceHistoryPointDto[];
  /**
   * Current Yes price (0–1) shown on the option rows. Anchors the line's right
   * end at `domainEndTs` so it reaches "now" and agrees with the buy bar;
   * absent (closed/settled charts) the line freezes at its last trade.
   */
  currentPrice?: number | null;
}

export interface MarketPriceChartProps {
  /** One line per market (single-market → exactly one). */
  lines: MarketChartLine[];
  /**
   * Left edge of the x domain (unix seconds) — the selected timeframe's window
   * start, so "1D" spans a day even when trades only cover part of it. Null/absent
   * → earliest data point (the "All" timeframe).
   */
  domainStartTs?: number | null;
  /**
   * Right edge of the x domain (unix seconds, the container's frozen "now").
   * Only honoured when at least one line is anchored by a `currentPrice`;
   * otherwise (frozen/closed chart) the domain ends at the last data point.
   */
  domainEndTs?: number | null;
}

/**
 * Chart-local greys per theme (chart-specific by design; the rest of the app
 * doesn't use this scale). Light keeps the Figma literals; dark picks from the
 * violet-tinted neutral ramp in index.css so the near-white ghost/crosshair
 * doesn't glare on the deep base.
 */
const CHART_GREYS: Record<Theme, { ghost: string; grid: string; axisX: string; axisY: string }> = {
  light: { ghost: '#f4f5f6', grid: '#aeb4bc', axisX: '#caced3', axisY: '#77808d' },
  dark: { ghost: '#4a3e60', grid: '#6b5c85', axisX: '#8b7da8', axisY: '#a99dbf' },
};

/*
 * Plot geometry: 10px top margin + 200px plot + 30px x-axis strip = 240px tall.
 * The right side reserves a 140px gutter where the series name/value labels
 * live; gridlines and the y-axis extend past the line area into that gutter.
 */
const CHART_HEIGHT = 240;
const MARGIN_TOP = 10;
const PLOT_HEIGHT = 200;
const LABEL_GUTTER = 140;
const GRID_RIGHT_INSET = 38;
const AXIS_RIGHT_INSET = 42;
const LABEL_X_OFFSET = 15;
const LABEL_HEIGHT = 43; // name line + gap + value line
const LABEL_MIN_GAP = 55; // min vertical spacing between two series labels
const Y_TICK_STEP = 0.15; // y domain rounds up to 15% steps

interface Series {
  id: string;
  label: string;
  color: string;
  points: SeriesPoint[];
}

/** Cursor-anchored hover: crosshair x plus each series' on-curve dot. */
interface HoverState {
  t: number; // hovered time (unix seconds), continuous
  x: number; // cursor x within the line area (px)
  perSeries: { id: string; color: string; x: number; y: number; p: number }[];
}

/** Short month for long ranges, month+day for weeks, time-of-day intraday. */
function makeXTickFormat(spanDays: number) {
  return (v: Date | number | { valueOf(): number }) => {
    const d = new Date(v.valueOf());
    if (spanDays > 90) return d.toLocaleDateString('en-US', { month: 'short' });
    if (spanDays > 2) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };
}

function formatHoverDate(tSec: number): string {
  const d = new Date(tSec * 1000);
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
}

/**
 * Market price chart (ARC-82 §5.2).
 *
 * Yes-probability-over-time, hand-composed from @visx primitives (axes, grid
 * rows, monotone-x line paths) with framer-motion animation. Each series draws
 * three stacked paths: a background-colored halo (which doubles as the "future"
 * ghost segment), the colored line (clipped at the cursor on hover so the part
 * after the crosshair falls back to the ghost), and an idle shimmer overlay.
 * Series name/value labels are HTML overlays kept readable over the lines via
 * a text-stroke knockout; hovering moves them to the cursor with collision
 * spacing and shows a crosshair + date label.
 */
export function MarketPriceChart({ lines, domainStartTs, domainEndTs }: MarketPriceChartProps) {
  const greys = CHART_GREYS[useTheme()];
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<HoverState | null>(null);
  // Halo <path> per series id — measured on hover to place the cursor dot on the curve.
  const pathRefs = useRef(new Map<string, SVGPathElement | null>());

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const series: Series[] = useMemo(
    () =>
      lines
        .map((l) => ({
          id: l.id,
          label: l.label,
          color: l.color,
          points: buildSeriesPoints(l.history, { currentPrice: l.currentPrice, endTs: domainEndTs }),
        }))
        .filter((s) => s.points.length > 1),
    [lines, domainEndTs],
  );

  const lineWidth = Math.max(width - LABEL_GUTTER, 0);
  const gridWidth = Math.max(width - GRID_RIGHT_INSET, 0);
  const axisRightX = width - AXIS_RIGHT_INSET;

  const { xScale, yScale, spanDays } = useMemo(() => {
    const allT = series.flatMap((s) => [s.points[0].t, s.points[s.points.length - 1].t]);
    // Anchored (open) lines reach the frozen "now"; a frozen/closed chart ends
    // at its last trade. The left edge honours the selected window so "1D"
    // spans a day even when trades cover only part of it.
    const anchored = lines.some((l) => l.currentPrice != null);
    const tMin = domainStartTs ?? (allT.length ? Math.min(...allT) : 0);
    const tMax = anchored && domainEndTs != null ? domainEndTs : allT.length ? Math.max(...allT) : 1;
    const pMax = Math.max(...series.flatMap((s) => s.points.map((d) => d.p)), 0.01);
    // Probability axis: round up to the next 15% step, but never past 100%.
    const yMax = Math.min(Math.ceil(pMax / Y_TICK_STEP - 1e-9) * Y_TICK_STEP, 1);
    return {
      spanDays: (tMax - tMin) / 86400,
      xScale: scaleTime<number>({ domain: [new Date(tMin * 1000), new Date(tMax * 1000)], range: [0, lineWidth] }),
      yScale: scaleLinear<number>({ domain: [0, yMax], range: [PLOT_HEIGHT, 0] }),
    };
  }, [series, lines, domainStartTs, domainEndTs, lineWidth]);

  const yTickValues = useMemo(() => {
    const [, yMax] = yScale.domain();
    const ticks: number[] = [];
    for (let v = 0; v <= yMax + 1e-9; v += Y_TICK_STEP) ticks.push(Number(v.toFixed(2)));
    // The 100% cap isn't on the 15% grid — keep it labelled so the top gridline has a value.
    if (ticks[ticks.length - 1] < yMax - 1e-9) ticks.push(yMax);
    return ticks;
  }, [yScale]);

  const xNumTicks = useMemo(() => {
    const fit = Math.min(11, Math.floor(width / 76)) || 2;
    // day-granular labels: at most one tick per day so labels don't repeat
    return spanDays > 2 && spanDays <= 10 ? Math.min(Math.round(spanDays), fit) : fit;
  }, [spanDays, width]);

  // Label placement: block bottoms out at the dot, clamped into the plot,
  // collisions resolved downward with a fixed minimum gap.
  const labels = useMemo(() => {
    const placed = series
      .map((s) => {
        const h = hover?.perSeries.find((ps) => ps.id === s.id);
        const last = s.points[s.points.length - 1];
        const value = h ? h.p : last.p;
        const dotY = h ? h.y : yScale(last.p);
        return { id: s.id, label: s.label, color: s.color, value, top: Math.max(dotY - LABEL_HEIGHT, 0) };
      })
      .sort((a, b) => a.top - b.top);
    for (let i = 1; i < placed.length; i++) {
      placed[i].top = Math.max(placed[i].top, placed[i - 1].top + LABEL_MIN_GAP);
    }
    return placed;
  }, [series, hover, yScale]);

  const labelX = (hover ? hover.x : lineWidth) + LABEL_X_OFFSET;

  // Hover tracks the cursor x continuously (no snapping to data points):
  // crosshair/clip/date sit at the cursor, and each series' dot is the exact
  // point on its rendered curve at that x, so the whole thing glides. Curve
  // measurement reads the halo <path> DOM nodes, so it lives in the pointer
  // handler (refs must not be read during render) and the result is state.
  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!svgRef.current || series.length === 0) return;
      const local = localPoint(svgRef.current, e);
      if (!local) return;
      const cursorX = Math.max(0, Math.min(local.x, lineWidth));
      const t = (xScale.invert(cursorX) as Date).getTime() / 1000;
      const perSeries = series.map((s) => {
        const el = pathRefs.current.get(s.id);
        if (el) {
          const pt = pointOnPathAtX(el, cursorX);
          return { id: s.id, color: s.color, x: pt.x, y: pt.y, p: yScale.invert(pt.y) };
        }
        const last = s.points[s.points.length - 1];
        return { id: s.id, color: s.color, x: xScale(new Date(last.t * 1000)), y: yScale(last.p), p: last.p };
      });
      setHover({ t, perSeries, x: cursorX });
    },
    [series, lineWidth, xScale, yScale],
  );

  const getX = useCallback((d: SeriesPoint) => xScale(new Date(d.t * 1000)) ?? 0, [xScale]);
  const getY = useCallback((d: SeriesPoint) => yScale(d.p) ?? 0, [yScale]);

  if (width > 0 && series.length === 0) {
    return (
      <div ref={containerRef} className="relative w-full" style={{ height: CHART_HEIGHT }}>
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted">No price history yet.</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: CHART_HEIGHT }}>
      {width > 0 && (
        <motion.div className="relative h-full w-full overflow-visible" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <svg ref={svgRef} width={width} height={CHART_HEIGHT} className="overflow-visible">
            <Group top={MARGIN_TOP}>
              <AxisBottom
                top={PLOT_HEIGHT}
                scale={xScale}
                hideAxisLine
                hideTicks
                numTicks={xNumTicks}
                tickFormat={makeXTickFormat(spanDays)}
                tickLabelProps={() => ({
                  fill: greys.axisX,
                  fontFamily: 'Arial',
                  fontSize: 12,
                  fontWeight: 400,
                  textAnchor: 'middle' as const,
                  dy: 12,
                })}
              />
              <AxisRight
                left={axisRightX}
                scale={yScale}
                hideAxisLine
                hideTicks
                tickValues={yTickValues}
                tickFormat={(v) => `${Math.round(Number(v) * 100)}%`}
                tickLabelProps={() => ({
                  fill: greys.axisY,
                  fontFamily: 'Arial',
                  fontSize: 12,
                  fontWeight: 400,
                  textAnchor: 'start' as const,
                  dx: 8,
                  dy: 4,
                })}
              />
              <GridRows
                scale={yScale}
                width={gridWidth}
                tickValues={yTickValues}
                stroke={greys.grid}
                strokeWidth={1}
                strokeDasharray="1,3"
                strokeOpacity={0.5}
                shapeRendering="crispEdges"
                pointerEvents="none"
              />

              {hover && (
                <line x1={hover.x} x2={hover.x} y1={-18} y2={PLOT_HEIGHT + 6} stroke={greys.ghost} strokeWidth={1.5} />
              )}

              <g>
                {series.map((s) => {
                  const clip = hover
                    ? `inset(-13px ${Math.max(lineWidth - hover.x + 2, -13)}px -13px -13px)`
                    : 'inset(-13px -13px -13px -13px)';
                  return (
                    <Group key={s.id}>
                      <LinePath
                        innerRef={(el: SVGPathElement | null) => {
                          pathRefs.current.set(s.id, el);
                        }}
                        data={s.points}
                        x={getX}
                        y={getY}
                        curve={curveMonotoneX}
                        stroke={greys.ghost}
                        strokeWidth={2}
                        fill="transparent"
                        className="pointer-events-none"
                      />
                      {/* Cursor clip lives on a plain <g>: framer-motion snapshots
                          style.clipPath and won't track per-pointermove updates. */}
                      <g style={{ clipPath: clip }}>
                        <LinePath data={s.points} x={getX} y={getY} curve={curveMonotoneX}>
                          {({ path }) => (
                            <motion.path
                              d={path(s.points) || ''}
                              stroke={s.color}
                              strokeWidth={1.75}
                              fill="transparent"
                              shapeRendering="geometricPrecision"
                              className="pointer-events-none"
                              initial={{ pathLength: 0 }}
                              animate={{ pathLength: 1 }}
                              transition={{ duration: 1, ease: 'easeInOut' }}
                            />
                          )}
                        </LinePath>
                      </g>
                    </Group>
                  );
                })}

                {!hover &&
                  series.map((s) => {
                    const last = s.points[s.points.length - 1];
                    const cx = getX(last);
                    const cy = getY(last);
                    return (
                      <g key={s.id} className="pointer-events-none">
                        <circle cx={cx} cy={cy} r={4} fill={s.color} />
                        <motion.circle
                          cx={cx}
                          cy={cy}
                          r={4}
                          fill={s.color}
                          style={{ transformOrigin: '50% 50%', transformBox: 'fill-box' }}
                          initial={{ scale: 1, opacity: 0.4 }}
                          animate={{ scale: 4, opacity: 0 }}
                          transition={{ duration: 1.8, ease: 'easeOut', repeat: Infinity, repeatDelay: 0.4 }}
                        />
                      </g>
                    );
                  })}
                {hover &&
                  hover.perSeries.map((h) => (
                    <circle
                      key={h.id}
                      cx={h.x}
                      cy={h.y}
                      r={4}
                      fill={h.color}
                      stroke={h.color}
                      strokeWidth={2}
                      className="pointer-events-none"
                    />
                  ))}
              </g>
            </Group>

            <rect
              x={0}
              y={0}
              width={width}
              height={CHART_HEIGHT}
              fill="transparent"
              onPointerMove={onPointerMove}
              onPointerLeave={() => setHover(null)}
            />
          </svg>

          <div
            className="pointer-events-none absolute left-0 w-full overflow-visible"
            style={{ top: MARGIN_TOP, height: PLOT_HEIGHT }}
          >
            {labels.map((l) => (
              <div
                key={l.id}
                className="pointer-events-none absolute left-0 top-0 z-10"
                style={{ transform: `translateX(${labelX}px) translateY(${l.top}px)` }}
              >
                <div className="flex flex-col items-start gap-[3.3px]">
                  {/* Knockout stroke matches the backdrop behind the chart: the page
                      gradient has already settled on --color-background-end there,
                      so --color-background would halo in both themes. */}
                  <span
                    className="whitespace-nowrap text-[13px] font-medium leading-[14px]"
                    style={{ color: l.color, WebkitTextStroke: '2px var(--color-background-end)', paintOrder: 'stroke' }}
                  >
                    {l.label}
                  </span>
                  <span
                    className="whitespace-nowrap text-[26px] font-semibold leading-none"
                    style={{ color: l.color, WebkitTextStroke: '2px var(--color-background-end)', paintOrder: 'stroke' }}
                  >
                    {(l.value * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}

            <AnimatePresence>
              {hover && (
                <motion.div
                  className="pointer-events-none absolute text-xs font-medium text-muted"
                  style={{ left: hover.x + 8, top: -24 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.12 }}
                >
                  {formatHoverDate(hover.t)}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </div>
  );
}
